/**
 * Regole/Filtri automatici: lista + editor (condizioni AND/OR → azioni).
 * Applicate dal sync worker ai nuovi messaggi in arrivo.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Loader2, X, Filter, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { rulesApi, type Rule, type RuleInput, type RuleCondition, type RuleAction, type RuleField, type RuleOp, type RuleActionType } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

const FIELD_LABEL: Record<RuleField, string> = { from: 'Mittente', to: 'Destinatario', subject: 'Oggetto' };
const OP_LABEL: Record<RuleOp, string> = {
  contains: 'contiene', not_contains: 'non contiene', equals: 'è uguale a', starts_with: 'inizia con', ends_with: 'finisce con',
};
const ACTION_LABEL: Record<RuleActionType, string> = {
  mark_read: 'Segna come letto', star: 'Aggiungi stella', set_category: 'Imposta categoria', archive: 'Archivia', trash: 'Cestina',
};

const EMPTY: RuleInput = {
  name: '',
  is_active: true,
  priority: 100,
  conditions: { match: 'all', conditions: [{ field: 'from', op: 'contains', value: '' }] },
  actions: [{ type: 'mark_read' }],
};

export function RulesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Rule | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<RuleInput>(EMPTY);

  const rulesQuery = useQuery({ queryKey: ['rules'], queryFn: () => rulesApi.list() });
  const rules = rulesQuery.data?.rules ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['rules'] });

  const saveMutation = useMutation({
    mutationFn: () => (editing ? rulesApi.update(editing.id, form) : rulesApi.create(form)),
    onSuccess: () => { invalidate(); close(); toast.success(editing ? 'Regola aggiornata' : 'Regola creata'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Errore salvataggio'),
  });
  const delMutation = useMutation({
    mutationFn: (id: string) => rulesApi.remove(id),
    onSuccess: () => { invalidate(); toast.success('Regola eliminata'); },
  });
  const toggleMutation = useMutation({
    mutationFn: (r: Rule) => rulesApi.update(r.id, { is_active: !r.is_active }),
    onSuccess: invalidate,
  });

  function openCreate() { setEditing(null); setForm(structuredClone(EMPTY)); setOpen(true); }
  function openEdit(r: Rule) {
    setEditing(r);
    setForm({ name: r.name, is_active: r.is_active, priority: r.priority, conditions: structuredClone(r.conditions), actions: structuredClone(r.actions) });
    setOpen(true);
  }
  function close() { setOpen(false); setEditing(null); setForm(EMPTY); }

  // condition helpers
  const setCond = (i: number, patch: Partial<RuleCondition>) =>
    setForm((f) => ({ ...f, conditions: { ...f.conditions, conditions: f.conditions.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) } }));
  const addCond = () => setForm((f) => ({ ...f, conditions: { ...f.conditions, conditions: [...f.conditions.conditions, { field: 'subject', op: 'contains', value: '' }] } }));
  const removeCond = (i: number) => setForm((f) => ({ ...f, conditions: { ...f.conditions, conditions: f.conditions.conditions.filter((_, idx) => idx !== i) } }));
  // action helpers
  const setAct = (i: number, patch: Partial<RuleAction>) => setForm((f) => ({ ...f, actions: f.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const addAct = () => setForm((f) => ({ ...f, actions: [...f.actions, { type: 'star' }] }));
  const removeAct = (i: number) => setForm((f) => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-8 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2"><Filter className="h-5 w-5" /> Regole e filtri</h1>
              <p className="text-sm text-muted-foreground">Automatizza la posta in arrivo: condizioni → azioni.</p>
            </div>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Nuova regola</Button>
          </div>

          {rulesQuery.isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rules.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Wand2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Nessuna regola. Creane una per smistare la posta automaticamente.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} className="rounded-lg border p-4">
                  <div className="flex items-start gap-3">
                    <Switch checked={r.is_active} onCheckedChange={() => toggleMutation.mutate(r)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Se <b>{r.conditions.match === 'all' ? 'tutte' : 'almeno una'}</b>: {r.conditions.conditions.map((c) => `${FIELD_LABEL[c.field]} ${OP_LABEL[c.op]} "${c.value}"`).join(', ')}
                      </div>
                      <div className="text-xs text-primary mt-0.5">
                        → {r.actions.map((a) => ACTION_LABEL[a.type] + (a.value ? ` (${a.value})` : '')).join(', ')}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { if (confirm('Eliminare la regola?')) delMutation.mutate(r.id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Modifica regola' : 'Nuova regola'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Fatture in Archivio" />
            </div>

            <div>
              <Label>Quando</Label>
              <select className="mt-1 mb-2 w-full border rounded-md bg-background px-3 py-2 text-sm"
                value={form.conditions.match}
                onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, match: e.target.value as 'all' | 'any' } })}>
                <option value="all">Tutte le condizioni sono vere</option>
                <option value="any">Almeno una condizione è vera</option>
              </select>
              <div className="space-y-2">
                {form.conditions.conditions.map((c, i) => (
                  <div key={i} className="flex gap-1.5 items-center">
                    <select className="border rounded-md bg-background px-2 py-1.5 text-sm" value={c.field} onChange={(e) => setCond(i, { field: e.target.value as RuleField })}>
                      {(['from', 'to', 'subject'] as RuleField[]).map((f) => <option key={f} value={f}>{FIELD_LABEL[f]}</option>)}
                    </select>
                    <select className="border rounded-md bg-background px-2 py-1.5 text-sm" value={c.op} onChange={(e) => setCond(i, { op: e.target.value as RuleOp })}>
                      {(Object.keys(OP_LABEL) as RuleOp[]).map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
                    </select>
                    <Input className="flex-1 h-8" value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="valore" />
                    {form.conditions.conditions.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => removeCond(i)}><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-1" onClick={addCond}><Plus className="h-3.5 w-3.5 mr-1" /> Condizione</Button>
            </div>

            <div>
              <Label>Allora</Label>
              <div className="space-y-2 mt-1">
                {form.actions.map((a, i) => (
                  <div key={i} className="flex gap-1.5 items-center">
                    <select className="border rounded-md bg-background px-2 py-1.5 text-sm flex-1" value={a.type} onChange={(e) => setAct(i, { type: e.target.value as RuleActionType, value: undefined })}>
                      {(Object.keys(ACTION_LABEL) as RuleActionType[]).map((t) => <option key={t} value={t}>{ACTION_LABEL[t]}</option>)}
                    </select>
                    {a.type === 'set_category' && (
                      <Input className="flex-1 h-8" value={a.value ?? ''} onChange={(e) => setAct(i, { value: e.target.value })} placeholder="categoria" />
                    )}
                    {form.actions.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => removeAct(i)}><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-1" onClick={addAct}><Plus className="h-3.5 w-3.5 mr-1" /> Azione</Button>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Priorità (più basso = prima)</Label>
                <Input type="number" min={1} max={1000} value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 100 })} className="w-28" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active ?? true} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Attiva</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Annulla</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
